/**
 * Data fetching hook - all React Query calls for product data
 * Extracted from ProductAttributeProvider (was 607 lines)
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { request } from '../../../../Infrastructure'
import { QUERY_STALE_TIME } from '../../lib/constants'
import { catalogKeys } from '../../lib/queryKeys'
import {
  getProductDescription,
  getProductRelated,
  getProductAttributes,
  getAutoRelatedSkus,
  getAutoRelatedProducts,
  getProductRewardPoints,
} from '../../queries'

import type {
  ProductAttributes,
  ProductDescription,
  ProductRelated,
  AutoRelatedProduct,
  ProductsQueryResponse,
  AutoRelatedSkusResponse,
  RewardPointsItem,
} from '../../types'

const QUERY_OPTIONS = {
  staleTime: QUERY_STALE_TIME,
  refetchOnWindowFocus: false,
} as const

export interface ProductDataResult {
  // Derived data
  productDescription: ProductDescription | null
  productAttributes: ProductAttributes | null
  productRelated: ProductRelated | null
  autoRelatedProducts: AutoRelatedProduct[]
  rewardPoints: number
  // Granular loading states
  loadingAttributes: boolean
  loadingDescription: boolean
  loadingRelated: boolean
  loadingAutoRelated: boolean
  loadingRewardPoints: boolean
  // Errors
  errors: unknown[]
}

export function useProductData(productSku: string): ProductDataResult {
  const enabled = !!productSku

  // Product description
  const productDetailsQuery = useQuery({
    queryKey: catalogKeys.description(productSku),
    queryFn: () => request<ProductsQueryResponse<ProductDescription>>(getProductDescription, { sku: productSku }),
    enabled,
    ...QUERY_OPTIONS,
  })

  // Product attributes (main product data)
  const productAttributesQuery = useQuery({
    queryKey: catalogKeys.attribute(productSku),
    queryFn: () => request<ProductsQueryResponse<ProductAttributes>>(getProductAttributes, { sku: productSku }),
    enabled,
    ...QUERY_OPTIONS,
  })

  // Related, upsell, cross-sell products
  const productRelatedQuery = useQuery({
    queryKey: catalogKeys.relatedBySku(productSku),
    queryFn: () => request<ProductsQueryResponse<ProductRelated>>(getProductRelated, { sku: productSku }),
    enabled,
    ...QUERY_OPTIONS,
  })

  // Reward points
  const productRewardPointsQuery = useQuery({
    queryKey: catalogKeys.rewardPoints(productSku),
    queryFn: () => request<ProductsQueryResponse<RewardPointsItem>>(getProductRewardPoints, { sku: productSku }),
    enabled,
    ...QUERY_OPTIONS,
  })

  // Auto-related SKUs (first query)
  const autoRelatedSkusQuery = useQuery({
    queryKey: catalogKeys.autoRelatedSkus(productSku),
    queryFn: () => request<AutoRelatedSkusResponse>(getAutoRelatedSkus, { sku: productSku }),
    enabled,
    ...QUERY_OPTIONS,
  })

  // Derive auto-related SKUs from query
  const autoRelatedSkus = useMemo(
    () => autoRelatedSkusQuery.data?.data?.getAutoRelatedSkus?.map(
      (product) => product.sku
    ) ?? [],
    [autoRelatedSkusQuery.data]
  )

  // Auto-related products (depends on SKUs)
  const autoRelatedProductsQuery = useQuery({
    queryKey: catalogKeys.autoRelatedProducts(autoRelatedSkus),
    queryFn: () => request<ProductsQueryResponse<AutoRelatedProduct>>(getAutoRelatedProducts, { skus: autoRelatedSkus }),
    enabled: autoRelatedSkus.length > 0,
    ...QUERY_OPTIONS,
  })

  // Derive final data - trivial property access, React Compiler handles memoization
  const productDescription: ProductDescription | null =
    productDetailsQuery.data?.data?.products?.items[0] ?? null

  const productAttributes: ProductAttributes | null =
    productAttributesQuery.data?.data?.products?.items[0] ?? null

  const productRelated: ProductRelated | null =
    productRelatedQuery.data?.data?.products?.items[0] ?? null

  const autoRelatedProducts: AutoRelatedProduct[] =
    autoRelatedProductsQuery.data?.data?.products?.items ?? []

  const rewardPoints: number =
    productRewardPointsQuery.data?.data?.products?.items[0]?.reward_points ?? 0

  // Granular loading (isPending = no cached data yet)
  const loadingAttributes = productAttributesQuery.isPending
  const loadingDescription = productDetailsQuery.isPending
  const loadingRelated = productRelatedQuery.isPending
  const loadingAutoRelated = autoRelatedSkusQuery.isPending || autoRelatedProductsQuery.isPending
  const loadingRewardPoints = productRewardPointsQuery.isPending

  // Collect all errors
  const errors = [
    productAttributesQuery.error,
    productDetailsQuery.error,
    productRelatedQuery.error,
    autoRelatedSkusQuery.error,
    autoRelatedProductsQuery.error,
    productRewardPointsQuery.error,
  ].filter(Boolean)

  return {
    productDescription,
    productAttributes,
    productRelated,
    autoRelatedProducts,
    rewardPoints,
    loadingAttributes,
    loadingDescription,
    loadingRelated,
    loadingAutoRelated,
    loadingRewardPoints,
    errors,
  }
}
